import { useEffect, useState } from "react"
import { ArrowLeft, Check, Inbox, LoaderCircle, Lock, Send } from "lucide-react"
import { router } from "@inertiajs/react"

import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { Textarea } from "@/components/ui/textarea"
import { loadSampleOptions, priceSampleDiscount, sendSampleRequest } from "@/lib/sampleRequests"

import RequestAlert from "./RequestAlert"
import RequestCost from "./RequestCost"

const EMPTY = {
  project_name: "",
  title: "",
  subtitle: "",
  fiction: false,
  writing_style: "",
  genre: "",
  niche: "",
  note: "",
  discount_code: "",
}

// A value the customer cannot change because it comes off the writer they chose.
function Locked({ label, value, hint }) {
  return (
    <div className="flex flex-col gap-1.5">
      <Label>{label}</Label>
      <div className="flex h-9 items-center justify-between gap-2 rounded-md border border-border bg-secondary px-3 text-sm text-muted-foreground">
        <span>{value}</span>
        {hint === "lock" ? <Lock className="size-3.5" strokeWidth={2} /> : <span className="text-[11px]">{hint}</span>}
      </div>
    </div>
  )
}

// Ask a writer for a paid 1,000-word sample (UPDA-74). Replaces the placeholder
// that told the customer the form didn't exist yet.
//
// Compose → review → sent, matching the invitation flow on this same profile:
// both commit money to a named person, so neither sends on one click.
//
// The fiction checkbox is a SWAP, not an addition — ticking it replaces Niche
// with Writing style and Genre, because a sample is either fiction or it isn't.
// It renders only when the server says this writer is cleared for fiction; a
// writer without the clearance can never be asked for a fiction sample.
//
// A resumed Viewing Room sample passes its approved line as notice, shown over
// the form; onSent, when given, is told once the server has created the request.
export default function SampleRequestDialog({ open, freelancer, onOpenChange, notice, onSent }) {
  const [options, setOptions] = useState(null)
  const [form, setForm] = useState(EMPTY)
  const [pricing, setPricing] = useState(null)
  const [codeState, setCodeState] = useState({ applied: false, note: "" })
  const [step, setStep] = useState("compose")
  const [loading, setLoading] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [pricingCode, setPricingCode] = useState(false)
  const [error, setError] = useState("")
  const [finishProfile, setFinishProfile] = useState("")

  const name = freelancer?.penName ?? options?.freelancer?.name ?? "this writer"
  const firstName = name.split(" ")[0]

  useEffect(() => {
    if (!open || !freelancer?.id) return undefined

    const controller = new AbortController()
    setOptions(null)
    setForm(EMPTY)
    setPricing(null)
    setCodeState({ applied: false, note: "" })
    setStep("compose")
    setError("")
    setFinishProfile("")
    setLoading(true)

    loadSampleOptions(freelancer.id, { signal: controller.signal })
      .then((data) => {
        setOptions(data)
        setPricing(data.pricing)
        if (!data.eligible) setError(data.reason ?? "")
      })
      .catch((loadError) => {
        if (loadError.name !== "AbortError") setError(loadError.message)
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })

    return () => controller.abort()
  }, [open, freelancer?.id])

  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }))

  // Ticking fiction clears whichever branch is being left behind, so a hidden
  // field can never travel with the request and fail validation server-side.
  const toggleFiction = (checked) => {
    setForm((current) => ({
      ...current,
      fiction: checked === true,
      niche: checked === true ? "" : current.niche,
      writing_style: checked === true ? current.writing_style : "",
      genre: checked === true ? current.genre : "",
    }))
  }

  async function applyCode() {
    const code = form.discount_code.trim()
    if (!code || pricingCode) return

    setPricingCode(true)
    setError("")
    try {
      const data = await priceSampleDiscount(freelancer.id, code)
      setPricing(data.pricing)
      setCodeState({
        applied: true,
        note: data.type === "fixed" ? `$${data.value} off applied` : `${data.value}% off applied`,
      })
    } catch (codeError) {
      setPricing(options?.pricing ?? null)
      setCodeState({ applied: false, note: codeError.message })
    } finally {
      setPricingCode(false)
    }
  }

  async function submit() {
    if (submitting) return
    setSubmitting(true)
    setError("")
    setFinishProfile("")
    try {
      await sendSampleRequest(freelancer.id, form)
      setStep("sent")
      onSent?.()
    } catch (submitError) {
      setError(submitError.message)
      setFinishProfile(submitError.finishProfile ?? "")
      setStep("compose")
    } finally {
      setSubmitting(false)
    }
  }

  const branchComplete = form.fiction
    ? form.writing_style !== "" && form.genre !== ""
    : form.niche !== ""
  // A customer without the billing details the request needs is told first,
  // with the way to add them and come back here (billingFirst), not after Send.
  const billingFirst = options?.eligible ? options.billingFirst : null

  const complete =
    options?.eligible &&
    !billingFirst &&
    form.project_name.trim() !== "" &&
    form.title.trim() !== "" &&
    form.subtitle.trim() !== "" &&
    form.note.trim() !== "" &&
    branchComplete

  if (step === "sent") {
    return (
      <>
        <DialogHeader>
          <div className="mb-2 flex size-11 items-center justify-center rounded-full bg-done/10 text-done">
            <Check className="size-5" strokeWidth={2.5} />
          </div>
          <DialogTitle>Request sent to {firstName}</DialogTitle>
          <DialogDescription>
            You'll find it under Requests until {firstName} answers, and you can cancel it there any
            time before it's accepted.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Done</Button>
          <Button
            onClick={() => { onOpenChange(false); router.visit("/requests") }}
            className="gap-1.5"
          >
            <Inbox className="size-4" strokeWidth={2} />
            View requests
          </Button>
        </DialogFooter>
      </>
    )
  }

  if (step === "review") {
    return (
      <>
        <DialogHeader>
          <DialogTitle>Review your sample request</DialogTitle>
          <DialogDescription>Check it over before {firstName} sees it.</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 rounded-[12px] border border-border bg-secondary/45 p-4 text-[13px]">
          <div>
            <span className="text-ink-3">Writer</span>
            <div className="font-semibold">{name} · {options?.tier}</div>
          </div>
          <div>
            <span className="text-ink-3">Sample</span>
            <div className="font-semibold">
              {options?.wordCount?.toLocaleString()} words ·{" "}
              {form.fiction ? `Fiction · ${form.genre} · ${form.writing_style}` : form.niche}
            </div>
          </div>
          <div>
            <span className="text-ink-3">Title</span>
            <div className="font-semibold">{form.title}{form.subtitle ? ` — ${form.subtitle}` : ""}</div>
          </div>
          <div>
            <span className="text-ink-3">What you'd like to see</span>
            <div className="mt-1 whitespace-pre-wrap">{form.note}</div>
          </div>
        </div>
        <RequestCost pricing={pricing} />
        {error && <p role="alert" className="text-[12.5px] text-destructive">{error}</p>}
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={() => setStep("compose")} disabled={submitting}>
            <ArrowLeft className="size-4" />
            Back
          </Button>
          <Button onClick={submit} disabled={submitting} className="gap-1.5">
            {submitting ? <LoaderCircle className="size-4 animate-spin" /> : <Send className="size-4" />}
            {submitting ? "Sending…" : "Send request"}
          </Button>
        </DialogFooter>
      </>
    )
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Request a sample from {name}</DialogTitle>
        <DialogDescription>
          A paid {options?.wordCount?.toLocaleString() ?? "1,000"}-word sample at {firstName}'s tier.
          {firstName} can accept or decline; you're only charged if it's accepted.
        </DialogDescription>
      </DialogHeader>

      {notice && (
        <p role="status" className="rounded-[12px] border border-border bg-secondary px-[14px] py-[10px] text-[13px] text-foreground">
          {notice}
        </p>
      )}

      {loading ? (
        <div className="flex items-center justify-center gap-2 py-10 text-[13px] text-ink-3">
          <LoaderCircle className="size-4 animate-spin" />
          Opening the form…
        </div>
      ) : !options?.eligible ? (
        <div className="rounded-[12px] border border-border bg-secondary/40 p-5 text-center text-[13px] text-ink-3">
          {options?.reason ?? error ?? "This writer can't take a sample request right now."}
        </div>
      ) : billingFirst ? (
        <div className="flex flex-col items-start gap-3 rounded-[12px] border border-border bg-secondary/40 p-5 text-[13px] text-foreground">
          <p>{billingFirst.message}</p>
          <Button asChild size="sm">
            <a href={billingFirst.path}>Finish your profile</a>
          </Button>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <div className="grid grid-cols-2 gap-3 max-[479px]:grid-cols-1">
            <Locked label="Tier" value={options.tier} hint="lock" />
            <Locked label="Word count" value={options.wordCount.toLocaleString()} hint="fixed" />
          </div>
          <p className="-mt-1 text-[11px] text-ink-3">
            Both are set by the writer you picked — you can't change them here.
          </p>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sample-project-name">Project name</Label>
            <Input
              id="sample-project-name"
              value={form.project_name}
              onChange={(event) => set("project_name")(event.target.value)}
              placeholder="What you'll call this in your project list"
              maxLength={120}
            />
          </div>

          {options.fictionAllowed && (
            <div className="flex items-start gap-2.5">
              <Checkbox
                id="sample-fiction"
                checked={form.fiction}
                onCheckedChange={toggleFiction}
                className="mt-0.5"
              />
              <Label htmlFor="sample-fiction" className="flex-col items-start gap-0.5 text-[13px] font-normal">
                <span>This is fiction</span>
                <span className="text-[11.5px] text-ink-3">
                  Swaps the niche for a genre and writing style.
                </span>
              </Label>
            </div>
          )}

          <div className="grid grid-cols-2 gap-3 max-[479px]:grid-cols-1">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sample-title">Title</Label>
              <Input
                id="sample-title"
                value={form.title}
                onChange={(event) => set("title")(event.target.value)}
                maxLength={200}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sample-subtitle">Subtitle</Label>
              <Input
                id="sample-subtitle"
                value={form.subtitle}
                onChange={(event) => set("subtitle")(event.target.value)}
                maxLength={200}
              />
            </div>
          </div>

          {form.fiction ? (
            <div className="grid grid-cols-2 gap-3 max-[479px]:grid-cols-1">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="sample-style">Writing style</Label>
                <Select value={form.writing_style} onValueChange={set("writing_style")}>
                  <SelectTrigger id="sample-style">
                    <SelectValue placeholder="Select a style" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.writingStyles.map((style) => (
                      <SelectItem key={style} value={style}>{style}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="sample-genre">Genre</Label>
                <Select value={form.genre} onValueChange={set("genre")}>
                  <SelectTrigger id="sample-genre">
                    <SelectValue placeholder="Select a genre" />
                  </SelectTrigger>
                  <SelectContent>
                    {options.genres.map((genre) => (
                      <SelectItem key={genre} value={genre}>{genre}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="sample-niche">Niche</Label>
              <Select value={form.niche} onValueChange={set("niche")}>
                <SelectTrigger id="sample-niche">
                  <SelectValue placeholder="Select a niche" />
                </SelectTrigger>
                <SelectContent>
                  {options.niches.map((niche) => (
                    <SelectItem key={niche} value={niche}>{niche}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-[11px] text-ink-3">
                Limited to the niches {firstName}'s tier covers.
              </span>
            </div>
          )}

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sample-note">What you'd like to see</Label>
            <Textarea
              id="sample-note"
              value={form.note}
              onChange={(event) => set("note")(event.target.value)}
              placeholder="Voice, pacing, the scene you want written — anything that helps show you the right thing."
              rows={4}
              maxLength={2_000}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sample-discount">Discount code</Label>
            <div className="flex gap-2">
              <Input
                id="sample-discount"
                value={form.discount_code}
                onChange={(event) => {
                  set("discount_code")(event.target.value)
                  setCodeState({ applied: false, note: "" })
                }}
                placeholder="Optional"
                className="flex-1"
              />
              <Button
                variant="outline"
                onClick={applyCode}
                disabled={!form.discount_code.trim() || pricingCode}
              >
                {pricingCode ? <LoaderCircle className="size-4 animate-spin" /> : "Apply"}
              </Button>
            </div>
            {codeState.note && (
              <span className={codeState.applied ? "text-[11.5px] text-done" : "text-[11.5px] text-destructive"}>
                {codeState.note}
              </span>
            )}
          </div>

          <RequestCost pricing={pricing} />
        </div>
      )}

      {options?.eligible && <RequestAlert error={error} finishProfile={finishProfile} />}

      <DialogFooter>
        <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
        <Button onClick={() => setStep("review")} disabled={!complete || loading}>
          Review request
        </Button>
      </DialogFooter>
    </>
  )
}
